import { IonButton, IonIcon, IonInput, IonItem, IonLabel } from "@ionic/react";
import { arrowRedoCircleOutline, arrowUndoCircleOutline, settingsOutline } from "ionicons/icons";
import type React from "react";
import { periodSummaryText } from "../logic/dates";
import { applyMoneyInputText, getCurrencySymbol, parseMoneyInput } from "../logic/money";
import { calculateMetrics, getTodayDisplay } from "../logic/metrics";
import type { SpendingConfiguration } from "../types";
import { DashboardRow } from "./DashboardRow";
import { PotButton } from "./PotButton";

interface DashboardScreenProps {
  configuration: SpendingConfiguration;
  currentDayNumber: number;
  formatter: Intl.NumberFormat;
  maxMoneyAmount: number;
  redoStackByDay: Record<string, number[]>;
  setCurrentDayNumber: React.Dispatch<React.SetStateAction<number>>;
  setRedoStackByDay: React.Dispatch<React.SetStateAction<Record<string, number[]>>>;
  setSpendAmountText: React.Dispatch<React.SetStateAction<string>>;
  setSpendByDay: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  setUndoStackByDay: React.Dispatch<React.SetStateAction<Record<string, number[]>>>;
  showDayControls: boolean;
  spendAmountText: string;
  spendByDay: Record<string, number>;
  undoStackByDay: Record<string, number[]>;
  onFinishPeriod: () => void;
  onAddSpend?: (amount: number) => Promise<void>;
  onOpenSetup: () => void;
  onOpenPotSelector?: () => void;
  potName: string | null;
  onRedoSpend?: () => Promise<void>;
  onUndoSpend?: () => Promise<void>;
}

export function DashboardScreen({
  configuration,
  currentDayNumber,
  formatter,
  maxMoneyAmount,
  redoStackByDay,
  setCurrentDayNumber,
  setRedoStackByDay,
  setSpendAmountText,
  setSpendByDay,
  setUndoStackByDay,
  showDayControls,
  spendAmountText,
  spendByDay,
  undoStackByDay,
  onFinishPeriod,
  onAddSpend,
  onOpenSetup,
  onOpenPotSelector,
  potName,
  onRedoSpend,
  onUndoSpend
}: DashboardScreenProps) {
  const metrics = calculateMetrics(configuration, currentDayNumber, spendByDay);
  const todayDisplay = getTodayDisplay(metrics);
  const currencySymbol = getCurrencySymbol(formatter);
  const canUndo = (undoStackByDay[currentDayNumber] ?? []).length > 0;
  const canRedo = (redoStackByDay[currentDayNumber] ?? []).length > 0;

  function addSpend() {
    const amount = parseMoneyInput(spendAmountText);

    if (!amount || amount <= 0 || amount > maxMoneyAmount) {
      return;
    }

    if (onAddSpend) {
      void onAddSpend(amount);
      return;
    }

    setSpendByDay((current) => ({
      ...current,
      [currentDayNumber]: (current[currentDayNumber] ?? 0) + amount
    }));
    setUndoStackByDay((current) => ({
      ...current,
      [currentDayNumber]: [...(current[currentDayNumber] ?? []), amount]
    }));
    setRedoStackByDay((current) => ({ ...current, [currentDayNumber]: [] }));
    setSpendAmountText("");
  }

  function undoSpend() {
    const undoStack = undoStackByDay[currentDayNumber] ?? [];
    const amount = undoStack.at(-1);

    if (!amount) {
      return;
    }

    if (onUndoSpend) {
      void onUndoSpend();
      return;
    }

    setSpendByDay((current) => ({
      ...current,
      [currentDayNumber]: Math.max(0, (current[currentDayNumber] ?? 0) - amount)
    }));
    setUndoStackByDay((current) => ({
      ...current,
      [currentDayNumber]: undoStack.slice(0, -1)
    }));
    setRedoStackByDay((current) => ({
      ...current,
      [currentDayNumber]: [...(current[currentDayNumber] ?? []), amount]
    }));
  }

  function redoSpend() {
    const redoStack = redoStackByDay[currentDayNumber] ?? [];
    const amount = redoStack.at(-1);

    if (!amount) {
      return;
    }

    if (onRedoSpend) {
      void onRedoSpend();
      return;
    }

    setSpendByDay((current) => ({
      ...current,
      [currentDayNumber]: (current[currentDayNumber] ?? 0) + amount
    }));
    setRedoStackByDay((current) => ({
      ...current,
      [currentDayNumber]: redoStack.slice(0, -1)
    }));
    setUndoStackByDay((current) => ({
      ...current,
      [currentDayNumber]: [...(current[currentDayNumber] ?? []), amount]
    }));
  }

  function advanceDay() {
    if (currentDayNumber >= configuration.totalPeriodDays) {
      onFinishPeriod();
      return;
    }

    setCurrentDayNumber((current) => current + 1);
    setSpendAmountText("");
  }

  function goToPreviousDay() {
    setCurrentDayNumber((current) => Math.max(1, current - 1));
    setSpendAmountText("");
  }

  return (
    <main className="tracker-screen dashboard-screen">
      <section className="today-card">
        <h1 className="dashboard-title">Spending Tracker</h1>
        <div className="card-topline">
          <span>
            Day {currentDayNumber} of {configuration.totalPeriodDays}
          </span>
          <div className="card-actions">
            {onOpenPotSelector ? <PotButton potName={potName} onOpenPotSelector={onOpenPotSelector} /> : null}
            <IonButton className="settings-button" fill="clear" size="small" aria-label="Restart setup" onClick={onOpenSetup}>
              <IonIcon icon={settingsOutline} />
            </IonButton>
          </div>
        </div>

        <p>Remaining Today</p>
        <strong className={todayDisplay.isNegative ? "negative" : ""}>
          {formatter.format(todayDisplay.remainingAmount)}
        </strong>
        <span className={`status-pill ${todayDisplay.statusClass}`}>
          {todayDisplay.message(formatter)}
        </span>
      </section>

      <section className="spend-entry-panel">
        <IonItem lines="none" className="field-item compact-field money-field">
          <IonLabel position="stacked">Spend amount</IonLabel>
          <span className="currency-prefix" aria-hidden="true">
            {currencySymbol}
          </span>
          <IonInput
            inputmode="decimal"
            max={maxMoneyAmount}
            placeholder="Enter your spend"
            value={spendAmountText}
            onIonInput={(event) => applyMoneyInputText(event, setSpendAmountText, maxMoneyAmount)}
          />
        </IonItem>

        <div className="entry-actions">
          <IonButton className="entry-icon-button" fill="outline" disabled={!canUndo} aria-label="Undo spend" onClick={undoSpend}>
            <IonIcon slot="icon-only" icon={arrowUndoCircleOutline} />
          </IonButton>
          <IonButton className="entry-icon-button" fill="outline" disabled={!canRedo} aria-label="Redo spend" onClick={redoSpend}>
            <IonIcon slot="icon-only" icon={arrowRedoCircleOutline} />
          </IonButton>
          <IonButton color="primary" onClick={addSpend}>
            Add Spend
          </IonButton>
        </div>
      </section>

      <section className="dashboard-rows" aria-label="Allowance summary">
        <DashboardRow
          movementAmount={metrics.dailyAllowanceDifference}
          formatter={formatter}
          title="Today’s Allowance"
          value={metrics.todayAllowance}
        />
        <DashboardRow
          formatter={formatter}
          isNegative={metrics.spendToday > metrics.todayAllowance}
          title="Spent Today"
          value={metrics.spendToday}
        />
        <DashboardRow formatter={formatter} title="Period Balance" value={metrics.remainingPeriodBalance} />
        <div className="dashboard-row">
          <span>Days Left</span>
          <strong>{metrics.daysLeftIncludingToday}</strong>
        </div>
      </section>

      <section className="period-footer">
        <p>{periodSummaryText(configuration, formatter)}</p>
        {showDayControls ? (
          <div className="day-actions">
            <IonButton fill="clear" size="small" disabled={currentDayNumber <= 1} onClick={goToPreviousDay}>
              Previous Day
            </IonButton>
            <IonButton fill="clear" size="small" onClick={advanceDay}>
              {currentDayNumber >= configuration.totalPeriodDays ? "Finish Period" : "Next Day"}
            </IonButton>
          </div>
        ) : null}
      </section>
    </main>
  );
}
